/**
 * 数据字典管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Input, Select, Space, Modal, message, Form, Tabs, Tag, Card, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, SettingOutlined, DatabaseOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { dictTypeService, dictDataService } from '../../../../../services/dictService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import { initDictData, checkDictInitialized, resetDictData } from '../../../../../utils/dictInitializer';
import QueryArea, { QueryField } from '../../components/QueryArea';

const { TextArea } = Input;

interface DictType {
  id: number;
  dictName: string;
  dictType: string;
  status: number;
  remark: string;
  createTime: string;
}

interface DictData {
  id: number;
  dictSort: number;
  dictLabel: string;
  dictValue: string;
  dictType: string;
  cssClass: string;
  listClass: string;
  isDefault: string;
  status: number;
  createBy: string;
  createTime: string;
  updateBy: string;
  updateTime: string;
  remark: string;
}

const DictManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('type');
  const [typeDataSource, setTypeDataSource] = useState<DictType[]>([]);
  const [dataDataSource, setDataDataSource] = useState<DictData[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    status: '',
    dictType: '', // 添加字典类型过滤字段
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DictType | DictData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const loadingRef = useRef(false);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '字典名',
      type: 'input',
      placeholder: '搜索字典名称或类型...',
      width: 200,
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      placeholder: '选择状态',
      width: 120,
      options: [
        { label: '正常', value: '1' },
        { label: '停用', value: '0' },
      ],
    },
  ];

  // 字典类型表格列定义
  const typeColumns: ColumnsType<DictType> = [
    {
      title: '字典名称',
      dataIndex: 'dictName',
      key: 'dictName',
      width: 200,
    },
    {
      title: '字典类型',
      dataIndex: 'dictType',
      key: 'dictType',
      width: 200,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (value) => (
        <span className={`status-tag ${value === 1 ? 'active' : 'inactive'}`}>
          {value === 1 ? '正常' : '停用'}
        </span>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            type="primary" 
            size="small" 
            icon={<SettingOutlined />}
            onClick={() => handleDataManage(record)}
          >
            数据
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 字典数据表格列定义
  const dataColumns: ColumnsType<DictData> = [
    {
      title: '字典标签',
      dataIndex: 'dictLabel',
      key: 'dictLabel',
      width: 150,
    },
    {
      title: '字典键值',
      dataIndex: 'dictValue',
      key: 'dictValue',
      width: 150,
    },
    {
      title: '字典排序',
      dataIndex: 'dictSort',
      key: 'dictSort',
      width: 100,
      align: 'center',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (value) => (
        <span className={`status-tag ${value === 1 ? 'active' : 'inactive'}`}>
          {value === 1 ? '正常' : '停用'}
        </span>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 数据加载
  const loadTypeData = useCallback(async () => {
    // 防止重复调用
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };

      const response = await dictTypeService.getList(params);
      if (response.code === 200) {
        setTypeDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
        // 在加载数据时同时检查初始化状态（如果还没有检查过）
        // 使用数据总数来判断是否已初始化，避免额外的API调用
        setIsInitialized(prev => {
          if (!prev) {
            return response.data.total > 0;
          }
          return prev;
        });
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [current, pageSize, searchParams]);

  const loadDataData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };

      const response = await dictDataService.getList(params);
      if (response.code === 200) {
        setDataDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  // 标签页切换处理函数
  const handleTabChange = useCallback((key: string) => {
    
    // 如果切换到相同标签页，不执行任何操作
    if (key === activeTab) {
      return;
    }
    
    // 重置所有分页和选择状态
    setCurrent(1);
    setPageSize(10);
    setSelectedRowKeys([]);
    setTotal(0);
    
    // 如果切换到字典类型标签页，清空字典类型过滤
    if (key === 'type') {
      setSearchParams(prev => ({
        ...prev,
        dictType: '', // 清空字典类型过滤
      }));
    }
    
    // 清空数据源
    setTypeDataSource([]);
    setDataDataSource([]);
    
    // 设置新的标签页
    setActiveTab(key);
  }, [activeTab]);

  // 统一的数据加载 effect：合并 activeTab、searchParams、current、pageSize 的变化
  // 这样可以避免多个 useEffect 同时触发导致重复调用
  useEffect(() => {
    // 防止重复调用
    if (loadingRef.current) return;
    
    // 使用防抖机制，避免快速连续调用
    const timer = setTimeout(() => {
      if (activeTab === 'type') {
        loadTypeData();
      } else {
        loadDataData();
      }
    }, 100); // 100ms 防抖延迟
    
    return () => {
      clearTimeout(timer);
    };
  }, [activeTab, searchParams, current, pageSize, loadTypeData, loadDataData]);

  // 初始化数据字典
  const handleInitDict = async () => {
    setInitLoading(true);
    try {
      const result = await initDictData();
      if (result.success) {
        message.success('数据字典初始化成功');
        setIsInitialized(true);
        if (activeTab === 'type') {
          loadTypeData();
        } else {
          loadDataData();
        }
      } else {
        message.error(result.message || '初始化失败');
      }
    } catch (error) {
      console.error('初始化失败:', error);
      message.error('初始化失败，请稍后重试');
    } finally {
      setInitLoading(false);
    }
  };

  // 重置数据字典
  const handleResetDict = () => {
    Modal.confirm({
      title: '确认重置',
      content: '确定要重置所有数据字典吗？这将删除所有现有的字典类型和数据，无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setInitLoading(true);
        try {
          const result = await resetDictData();
          if (result.success) {
            message.success('数据字典重置成功');
            setIsInitialized(false);
            setTypeDataSource([]);
            setDataDataSource([]);
          } else {
            message.error(result.message || '重置失败');
          }
        } catch (error) {
          console.error('重置失败:', error);
          message.error('重置失败，请稍后重试');
        } finally {
          setInitLoading(false);
        }
      },
    });
  };

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
    if (activeTab === 'type') {
      loadTypeData();
    } else {
      loadDataData();
    }
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', status: '', dictType: '' });
    setCurrent(1);
    setTimeout(() => {
      if (activeTab === 'type') {
        loadTypeData();
      } else {
        loadDataData();
      }
    }, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams(prev => ({
      keyword: values.keyword || '',
      status: values.status || '',
      dictType: values.dictType !== undefined ? values.dictType : prev.dictType, // 保留dictType，如果values中没有则保留原值
    }));
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: DictType | DictData) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  // 数据管理
  const handleDataManage = (record: DictType) => {
    // 重置分页到第一页
    setCurrent(1);
    // 设置字典类型过滤参数并切换到字典数据标签页
    setSearchParams({
      keyword: '',
      status: '',
      dictType: record.dictType, // 设置选中的字典类型
    });
    // 切换到字典数据标签页（这会触发useEffect加载数据）
    setActiveTab('data');
    message.info(`已切换到字典数据管理：${record.dictName}，显示字典类型为"${record.dictType}"的数据`);
  };

  // 删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条记录吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          let response;
          if (activeTab === 'type') {
            response = await dictTypeService.delete(id);
          } else {
            response = await dictDataService.delete(id);
          }
          
          if (response.code === 200) {
            message.success('删除成功');
            if (activeTab === 'type') {
              loadTypeData();
            } else {
              loadDataData();
            }
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条记录吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          let response;
          if (activeTab === 'type') {
            response = await dictTypeService.batchDelete(selectedRowKeys as number[]);
          } else {
            response = await dictDataService.batchDelete(selectedRowKeys as number[]);
          }
          
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            if (activeTab === 'type') {
              loadTypeData();
            } else {
              loadDataData();
            }
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      let response;
      if (activeTab === 'type') {
        // 字典类型操作
        if (editingRecord) {
          response = await dictTypeService.update({ ...values, id: editingRecord.id });
        } else {
          response = await dictTypeService.create(values);
        }
      } else {
        // 字典数据操作
        if (editingRecord) {
          response = await dictDataService.update({ ...values, id: editingRecord.id });
        } else {
          response = await dictDataService.create(values);
        }
      }
      
      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setIsModalVisible(false);
        if (activeTab === 'type') {
          loadTypeData();
        } else {
          loadDataData();
        }
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dict-management">

      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange}
        items={[
          {
            key: 'type',
            label: '字典类型',
            children: (
              <>
                {/* 统一查询区域 */}
                <QueryArea
                  fields={queryFields}
                  values={searchParams}
                  onValuesChange={handleSearchParamsChange}
                  onAdd={handleAdd}
                  onBatchDelete={handleBatchDelete}
                  batchDeleteDisabled={selectedRowKeys.length === 0}
                  selectedCount={selectedRowKeys.length}
                  addButtonText="新增类型"
                  batchDeleteButtonText="批量删除"
                />

                {/* 数据表格 */}
                <div className="table-card">
                  <Table
                    columns={typeColumns}
                    dataSource={typeDataSource}
                    loading={loading}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      current,
                      pageSize,
                      total,
                      ...PAGINATION_CONFIG.default,
                      onChange: (page, size) => {
                        setCurrent(page);
                        setPageSize(size);
                      },
                    }}
                    scroll={{ x: 1200 }}
                  />
                </div>
              </>
            ),
          },
          {
            key: 'data',
            label: '字典数据',
            children: (
              <>
                {/* 统一查询区域 */}
                <QueryArea
                  fields={queryFields}
                  values={searchParams}
                  onValuesChange={handleSearchParamsChange}
                  onAdd={handleAdd}
                  onBatchDelete={handleBatchDelete}
                  batchDeleteDisabled={selectedRowKeys.length === 0}
                  selectedCount={selectedRowKeys.length}
                  addButtonText="新增数据"
                  batchDeleteButtonText="批量删除"
                />

                {/* 数据表格 */}
                <div className="table-card">
                  <Table
                    columns={dataColumns}
                    dataSource={dataDataSource}
                    loading={loading}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      current,
                      pageSize,
                      total,
                      ...PAGINATION_CONFIG.default,
                      onChange: (page, size) => {
                        setCurrent(page);
                        setPageSize(size);
                      },
                    }}
                    scroll={{ x: 1200 }}
                  />
                </div>
              </>
            ),
          },
        ]}
      />

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? `编辑${activeTab === 'type' ? '字典类型' : '字典数据'}` : `新增${activeTab === 'type' ? '字典类型' : '字典数据'}`}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          initialValues={{
            status: 1,
          }}
        >
          {activeTab === 'type' ? (
            <>
              {/* 第一排：字典名称和字典类型 */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="dictName"
                    label="字典名称:"
                    rules={[{ required: true, message: '请输入字典名称' }]}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典名称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dictType"
                    label="字典类型:"
                    rules={[{ required: true, message: '请输入字典类型' }]}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典类型" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第二排：状态 */}
              <Form.Item
                name="status"
                label="状态:"
                labelAlign="left"
              >
                <Select>
                  <Select.Option value={1}>正常</Select.Option>
                  <Select.Option value={0}>停用</Select.Option>
                </Select>
              </Form.Item>

              {/* 第三排：备注 */}
              <Form.Item
                name="remark"
                label="备注:"
                labelAlign="left"
              >
                <TextArea rows={3} placeholder="请输入备注" />
              </Form.Item>
            </>
          ) : (
            <>
              {/* 第一排：字典标签和字典键值 */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="dictLabel"
                    label="字典标签:"
                    rules={[{ required: true, message: '请输入字典标签' }]}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典标签" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dictValue"
                    label="字典键值:"
                    rules={[{ required: true, message: '请输入字典键值' }]}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典键值" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第二排：字典排序和字典类型 */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="dictSort"
                    label="字典排序:"
                    rules={[{ required: true, message: '请输入字典排序' }]}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典排序" type="number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dictType"
                    label="字典类型:"
                    rules={[{ required: true, message: '请输入字典类型' }]}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入字典类型" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第三排：样式属性和表格回显样式 */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="cssClass"
                    label="样式属性:"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入样式属性" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="listClass"
                    label="表格回显样式:"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                  >
                    <Input placeholder="请输入表格回显样式" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第四排：状态 */}
              <Form.Item
                name="status"
                label="状态:"
                labelAlign="left"
              >
                <Select>
                  <Select.Option value={1}>正常</Select.Option>
                  <Select.Option value={0}>停用</Select.Option>
                </Select>
              </Form.Item>

              {/* 第五排：备注 */}
              <Form.Item
                name="remark"
                label="备注:"
                labelAlign="left"
              >
                <TextArea rows={3} placeholder="请输入备注" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DictManagement;
